/**
 * Utilidad centralizada de formateo de fechas para Colombia.
 *
 * El backend guarda timestamps como "timestamp without time zone" en hora Colombia.
 * JavaScript los interpreta como UTC por defecto, causando un desfase de -5h.
 * Esta utilidad agrega el sufijo de timezone para que JS no aplique conversión.
 */

const COLOMBIA_OFFSET = '-05:00';

/**
 * Parsea un timestamp del backend (hora Colombia sin TZ) a un Date correcto.
 */
export function parseColombiaDate(dateStr: string | Date | null | undefined): Date | null {
  if (!dateStr) return null;
  let str = String(dateStr);
  // El backend guarda timestamps en hora Colombia pero GraphQL los serializa con "Z" (UTC).
  // Reemplazar "Z" por el offset real de Colombia.
  if (str.endsWith('Z')) {
    str = str.slice(0, -1) + COLOMBIA_OFFSET;
  }
  if (/[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }
  return new Date(str + COLOMBIA_OFFSET);
}

/**
 * Formatea un timestamp del backend a fecha y hora legible.
 * Ejemplo: "28/03/2026, 3:26 a. m."
 */
export function formatDateTime(dateStr: string | Date | null | undefined, options: Intl.DateTimeFormatOptions = {}): string {
  const date = parseColombiaDate(dateStr);
  if (!date || isNaN(date.getTime())) return 'Fecha inválida';

  return date.toLocaleString('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Bogota',
    ...options,
  });
}

/**
 * Formatea un timestamp del backend a solo fecha.
 * Ejemplo: "28/03/2026"
 */
export function formatDate(dateStr: string | Date | null | undefined, options: Intl.DateTimeFormatOptions = {}): string {
  const date = parseColombiaDate(dateStr);
  if (!date || isNaN(date.getTime())) return 'Fecha inválida';

  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/Bogota',
    ...options,
  });
}

/**
 * Formatea un timestamp del backend a solo hora.
 * Ejemplo: "3:26:45 a. m."
 */
export function formatTime(dateStr: string | Date | null | undefined): string {
  const date = parseColombiaDate(dateStr);
  if (!date || isNaN(date.getTime())) return '-';

  return date.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'America/Bogota',
  });
}

/**
 * Formatea fecha con mes corto.
 * Ejemplo: "28 mar. 2026"
 */
export function formatDateShort(dateStr: string | Date | null | undefined): string {
  const date = parseColombiaDate(dateStr);
  if (!date || isNaN(date.getTime())) return 'Fecha inválida';

  return date.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'America/Bogota',
  });
}
